import React from "react";
import ButtonBase from "@mui/material/ButtonBase";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { nestedSurfaceSx, toneMain } from "./channelTheme";
import { formatChannelValue, summarizeChannels } from "./channelUtils";

function StatTile({ icon, tone = "info", label, value, caption, onClick, active }) {
  const content = (
    <>
      <MDBox
        sx={({ palette, borders, functions: { rgba } }) => ({
          width: 36,
          height: 36,
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          borderRadius: borders.borderRadius.md,
          color: toneMain(palette, tone),
          backgroundColor: rgba(toneMain(palette, tone), 0.14),
        })}
      >
        <Icon className={icon} sx={{ fontSize: "1rem !important", width: "auto" }} />
      </MDBox>
      <MDBox minWidth={0} textAlign="left">
        <MDTypography variant="caption" color="text" display="block" noWrap>
          {label}
        </MDTypography>
        <MDTypography variant="h6" fontWeight="bold" lineHeight={1.2} noWrap>
          {value}
        </MDTypography>
        {caption && (
          <MDTypography variant="caption" color="text" display="block" noWrap sx={{ opacity: 0.7 }}>
            {caption}
          </MDTypography>
        )}
      </MDBox>
    </>
  );
  const sx = (theme) => ({
    ...nestedSurfaceSx(theme),
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 1.5,
    p: 1.5,
    width: "100%",
    minWidth: 0,
    ...(active && { borderColor: toneMain(theme.palette, tone) }),
    ...(onClick && {
      "&:hover": { backgroundColor: theme.functions.rgba(theme.palette.text.main, 0.07) },
      "&.Mui-focusVisible": { outline: `2px solid ${theme.palette.info.main}`, outlineOffset: 2 },
    }),
  });
  return onClick ? (
    <ButtonBase focusRipple onClick={onClick} aria-pressed={!!active} sx={sx}>
      {content}
    </ButtonBase>
  ) : (
    <MDBox sx={sx}>{content}</MDBox>
  );
}

export default function GatewayChannelSummary({ channels = [], filter, onFilterChange }) {
  const s = summarizeChannels(channels);
  const tiles = [
    {
      key: "total",
      icon: "fa-solid fa-tower-broadcast",
      tone: "info",
      label: "Channels",
      value: s.total,
      caption: `${new Set(channels.map((c) => c.group)).size} type groups`,
    },
    {
      key: "attention",
      icon: s.alarms ? "fa-solid fa-circle-exclamation" : "fa-solid fa-triangle-exclamation",
      tone: s.alarms ? "error" : s.attention ? "warning" : "success",
      label: "Need attention",
      value: s.attention,
      caption: s.attention ? `${s.alarms} critical · tap to filter` : "All channels normal",
      onClick: s.attention ? () => onFilterChange?.(filter === "attention" ? "all" : "attention") : undefined,
      active: filter === "attention",
    },
  ];
  if (s.fullestCollection) {
    tiles.push({
      key: "fullest",
      icon: "fa-solid fa-fill-drip",
      tone: "info",
      label: "Fullest holding tank",
      value: formatChannelValue(s.fullestCollection.value, "%", "level"),
      caption:
        s.fullestCollection.name +
        (s.collectionVolume !== null ? ` · ${formatChannelValue(s.collectionVolume, s.volumeUnit)} total` : ""),
    });
  }
  if (s.lowestSupply) {
    tiles.push({
      key: "supply",
      icon: "fa-solid fa-faucet-drip",
      tone: "info",
      label: "Lowest supply tank",
      value: formatChannelValue(s.lowestSupply.value, "%", "level"),
      caption: s.lowestSupply.name,
    });
  }
  if (s.tempRange) {
    tiles.push({
      key: "temp",
      icon: "fa-solid fa-temperature-half",
      tone: "info",
      label: "Temperature range",
      value:
        s.tempRange.min === s.tempRange.max
          ? formatChannelValue(s.tempRange.min, s.tempRange.unit)
          : `${formatChannelValue(s.tempRange.min, s.tempRange.unit)} – ${formatChannelValue(s.tempRange.max, s.tempRange.unit)}`,
    });
  }

  return (
    <MDBox display="grid" gap={1.5} gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))">
      {tiles.slice(0, 4).map(({ key, ...t }) => (
        <StatTile key={key} {...t} />
      ))}
    </MDBox>
  );
}
